import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { CalendarBlank, ArrowUpRight, ArrowDownRight, Minus, DownloadSimple } from "@phosphor-icons/react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { SourceTag } from "./PerfUI";

const todayISO = () => new Date().toISOString().slice(0, 10);
const frDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");

const fmt = (v, unit) => {
  if (v == null) return "—";
  const n = Number(v);
  const s = n.toLocaleString("fr-FR", { maximumFractionDigits: unit === "percent" ? 1 : 2 });
  if (unit === "currency") return `${s} €`;
  if (unit === "percent") return `${s} %`;
  if (unit === "duration") return `${s} min`;
  return s;
};

const BASIS_LABEL = { day: "jour", week: "semaine", month: "mois" };

export default function Performance() {
  const [period, setPeriod] = useState("day");
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [collab, setCollab] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendMetric, setTrendMetric] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get("/performance/export", {
        params: { date, period },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `performance_${period}_${date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch (e) {
      toast.error("Erreur pendant l'export Excel");
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        apiClient.get("/performance/summary", { params: { date, period } }),
        apiClient.get("/performance/summary/collaborators", { params: { date, period } }),
      ]);
      setData(s.data);
      setCollab(c.data);
      setTrendMetric((prev) => prev || s.data.metrics[0]?.metric_id || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement des résultats");
    } finally {
      setLoading(false);
    }
  }, [date, period]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!trendMetric) return;
    apiClient
      .get("/performance/trend", { params: { metric_ids: trendMetric, days: 14, date } })
      .then(({ data }) => setTrend(data))
      .catch(() => setTrend(null));
  }, [trendMetric, date]);

  const metrics = data?.metrics || [];
  const byService = useMemo(() => {
    const map = {};
    metrics.forEach((m) => {
      (map[m.service_id] = map[m.service_id] || []).push(m);
    });
    return map;
  }, [metrics]);

  const headline = useMemo(() => metrics.filter((m) => m.target != null).slice(0, 6), [metrics]);
  const trendMeta = trend?.metrics?.[0];

  return (
    <>
      <Topbar
        title="Performance"
        subtitle="Résultats jour · semaine · mois — lecture seule"
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 h-9 px-3 border border-zinc-300 bg-white">
              <CalendarBlank size={14} weight="bold" className="text-zinc-400" />
              <input
                type="date"
                value={date}
                max={todayISO()}
                onChange={(e) => setDate(e.target.value)}
                className="text-xs outline-none bg-transparent font-mono"
                data-testid="perf-date"
              />
            </div>
            <Link
              to="/performance"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 border border-zinc-300 bg-white text-xs text-zinc-700 hover:bg-zinc-50"
              data-testid="perf-link-proposal"
            >
              Vue synthétique
            </Link>
            <button
              onClick={exportXlsx}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 h-9 px-3 border border-zinc-300 bg-white text-xs sm:text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
              data-testid="perf-export-xlsx"
            >
              <DownloadSimple size={15} weight="bold" />
              {exporting ? "Export…" : "Export Excel"}
            </button>
            <div className="inline-flex border border-zinc-300 bg-white" data-testid="perf-period-switch">
              {[["day", "Jour"], ["week", "Semaine"], ["month", "Mois"]].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setPeriod(id)}
                  data-testid={`perf-period-${id}`}
                  className={`px-4 h-9 text-xs sm:text-sm transition-colors ${period === id ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        }
      />

      <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 max-w-[1600px] w-full" data-testid="perf-dashboard">
        {loading && !data ? (
          <div className="bg-white border border-zinc-200 px-5 py-10 text-center text-sm text-zinc-500">Chargement…</div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500 mb-5" data-testid="perf-range-info">
              <span>
                Période : <span className="font-mono text-zinc-700">{frDate(data.range.start)} → {frDate(data.range.end)}</span>
              </span>
              <span>
                Comparée à : <span className="font-mono text-zinc-700">{frDate(data.previous_range.start)} → {frDate(data.previous_range.end)}</span>
              </span>
              <span>{data.days_with_entries} jour(s) saisi(s) sur la période</span>
            </div>

            {data.days_with_entries === 0 && (
              <div className="border border-amber-300 bg-amber-50 px-4 py-3 mb-6 text-xs sm:text-sm text-amber-900" data-testid="perf-empty-warning">
                Aucune saisie sur cette période — les résultats affichent « — ». Renseignez une journée dans Admin Performance.
              </div>
            )}

            <Tabs defaultValue="global">
              <TabsList className="bg-zinc-100 rounded-none p-1 h-auto flex-wrap justify-start mb-6" data-testid="perf-tabs">
                <TabsTrigger value="global" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-xs sm:text-sm" data-testid="perf-tab-global">Vue globale</TabsTrigger>
                {data.services.map((s) => (
                  <TabsTrigger key={s.id} value={s.id} className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-xs sm:text-sm" data-testid={`perf-tab-${s.id}`}>
                    {s.name}
                  </TabsTrigger>
                ))}
                <TabsTrigger value="equipes" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-xs sm:text-sm" data-testid="perf-tab-equipes">Équipes</TabsTrigger>
              </TabsList>

              <TabsContent value="global">
                <Section title="Indicateurs clés" right={`${headline.length} indicateur(s) avec objectif`} />
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="perf-kpi-grid">
                  {headline.map((m) => <KpiCard key={m.metric_id} m={m} service={data.services.find((s) => s.id === m.service_id)?.name} />)}
                  {headline.length === 0 && <Empty text="Aucun objectif défini pour l'instant." />}
                </div>

                <Section
                  title="Évolution sur 14 jours"
                  right={
                    <Select value={trendMetric} onValueChange={setTrendMetric}>
                      <SelectTrigger className="h-9 rounded-sm border-zinc-300 w-[280px] text-xs" data-testid="perf-trend-select">
                        <SelectValue placeholder="Choisir un indicateur" />
                      </SelectTrigger>
                      <SelectContent>
                        {metrics.map((m) => (
                          <SelectItem key={m.metric_id} value={m.metric_id}>
                            {data.services.find((s) => s.id === m.service_id)?.name} — {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  }
                />
                <div className="bg-white border border-zinc-200 p-4 sm:p-5" data-testid="perf-chart-trend">
                  <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trend?.points || []} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                        <CartesianGrid stroke="#f1f1f3" vertical={false} />
                        <XAxis dataKey="date" tickFormatter={(d) => d.slice(8) + "/" + d.slice(5, 7)} tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
                        <Tooltip
                          contentStyle={{ borderRadius: 0, border: "1px solid #e4e4e7", fontSize: 12 }}
                          labelFormatter={(d) => frDate(d)}
                          formatter={(v) => fmt(v, trendMeta?.unit)}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {trendMetric && (
                          <Line type="monotone" dataKey={trendMetric} name={trendMeta?.name || "Indicateur"} stroke="#18181b" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <Section title="Résultats par service" right={`${data.services.length} services actifs`} />
                <MetricTable rows={metrics} services={data.services} showService />
              </TabsContent>

              {data.services.map((s) => (
                <TabsContent key={s.id} value={s.id}>
                  <Section title={s.name} right={`${(byService[s.id] || []).length} indicateur(s)`} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    {(byService[s.id] || []).map((m) => <KpiCard key={m.metric_id} m={m} />)}
                    {(byService[s.id] || []).length === 0 && <Empty text="Aucun indicateur actif pour ce service." />}
                  </div>
                  <Section title="Détail" />
                  <MetricTable rows={byService[s.id] || []} services={data.services} />
                </TabsContent>
              ))}

              <TabsContent value="equipes">
                <Section title="Résultats par collaborateur" right={`${collab?.rows?.length || 0} collaborateur(s)`} />
                <CollabTable collab={collab} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </>
  );
}

function KpiCard({ m, service }) {
  const good = m.on_track;
  const deltaGood = m.delta_pct == null ? null : m.direction === "down" ? m.delta_pct < 0 : m.delta_pct > 0;
  return (
    <div className="bg-white border border-zinc-200 p-4 sm:p-5 flex flex-col justify-between min-h-[150px]" data-testid={`perf-kpi-${m.metric_id}`}>
      <div>
        <div className="flex items-start justify-between gap-2">
          <div>
            {service && <div className="text-[10px] uppercase tracking-wide text-zinc-300 mb-0.5">{service}</div>}
            <div className="label-tiny text-zinc-400 leading-snug">{m.name}</div>
          </div>
          <SourceTag source={m.source} />
        </div>
        <div className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-2 tabular-nums">{fmt(m.value, m.unit)}</div>
      </div>
      <div className="mt-3 space-y-2">
        {m.target != null && (
          <>
            <div className="flex items-center justify-between text-[11px] text-zinc-500">
              <span>
                Objectif {fmt(m.target, m.unit)}
                {m.target_raw && <span className="text-zinc-400"> ({fmt(m.target_raw.value, m.unit)}/{BASIS_LABEL[m.target_raw.basis]})</span>}
              </span>
              {m.achievement != null && (
                <span className={`font-semibold tabular-nums ${good ? "text-green-700" : "text-red-600"}`}>{m.achievement} %</span>
              )}
            </div>
            <div className="h-1.5 bg-zinc-100">
              <div className={`h-full ${good ? "bg-green-600" : "bg-red-500"}`} style={{ width: `${Math.min(m.achievement ?? 0, 100)}%` }} />
            </div>
          </>
        )}
        {m.delta_pct != null && (
          <div className={`flex items-center gap-1 text-[11px] ${deltaGood ? "text-green-700" : "text-red-600"}`}>
            {m.delta_pct === 0 ? <Minus size={12} /> : deltaGood ? <ArrowUpRight size={12} weight="bold" /> : <ArrowDownRight size={12} weight="bold" />}
            <span className="tabular-nums font-medium">{m.delta_pct > 0 ? "+" : ""}{m.delta_pct} %</span>
            <span className="text-zinc-400">vs période précédente</span>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricTable({ rows, services, showService }) {
  if (rows.length === 0) return <Empty text="Aucun indicateur." />;
  return (
    <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="perf-service-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50">
            {showService && <Th>Service</Th>}
            <Th>Indicateur</Th><Th right>Réalisé</Th><Th right>Objectif</Th><Th right>Atteinte</Th><Th right>Période précédente</Th><Th right>Évolution</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m, i) => {
            const prevService = showService && i > 0 && rows[i - 1].service_id === m.service_id;
            const deltaGood = m.delta_pct == null ? null : m.direction === "down" ? m.delta_pct < 0 : m.delta_pct > 0;
            return (
              <tr key={m.metric_id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`perf-row-${m.metric_id}`}>
                {showService && <td className="px-4 py-3 font-medium whitespace-nowrap">{prevService ? "" : services.find((s) => s.id === m.service_id)?.name}</td>}
                <td className="px-4 py-3 text-zinc-600">
                  <span className="inline-flex items-center gap-2">{m.name}<SourceTag source={m.source} /></span>
                  {m.snapshot && <div className="text-[10px] text-zinc-400 mt-0.5">état de fin de journée</div>}
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmt(m.value, m.unit)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-zinc-500">{fmt(m.target, m.unit)}</td>
                <td className={`px-4 py-3 text-right tabular-nums font-semibold ${m.achievement == null ? "text-zinc-400" : m.on_track ? "text-green-700" : "text-red-600"}`}>
                  {m.achievement == null ? "—" : `${m.achievement} %`}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-zinc-400">{fmt(m.previous, m.unit)}</td>
                <td className={`px-4 py-3 text-right tabular-nums ${m.delta_pct == null ? "text-zinc-400" : deltaGood ? "text-green-700" : "text-red-600"}`}>
                  {m.delta_pct == null ? "—" : `${m.delta_pct > 0 ? "+" : ""}${m.delta_pct} %`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CollabTable({ collab }) {
  if (!collab || collab.rows.length === 0) return <Empty text="Aucun collaborateur suivi. Ajoutez-les dans Admin Performance." />;
  return (
    <div className="space-y-6">
      {[...new Set(collab.rows.map((r) => r.service_id))].map((sid) => {
        const cols = collab.metrics.filter((m) => m.service_id === sid);
        const rows = collab.rows.filter((r) => r.service_id === sid);
        if (cols.length === 0) return null;
        return (
          <div key={sid} className="bg-white border border-zinc-200 overflow-x-auto" data-testid={`perf-collab-table-${sid}`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <Th>Collaborateur</Th>
                  {cols.map((c) => <Th key={c.metric_id} right>{c.name}</Th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.collaborator_id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                    <td className="px-4 py-3 font-medium">{r.name}</td>
                    {cols.map((c) => (
                      <td key={c.metric_id} className="px-4 py-3 text-right tabular-nums">{fmt(r.values[c.metric_id], c.unit)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

const Section = ({ title, right }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 mb-3 mt-8 first:mt-0">
    <h2 className="font-heading text-lg font-semibold tracking-tight">{title}</h2>
    {typeof right === "string" ? <span className="label-tiny text-zinc-400">{right}</span> : right}
  </div>
);

const Empty = ({ text }) => (
  <div className="bg-white border border-dashed border-zinc-300 px-5 py-8 text-center text-sm text-zinc-500 col-span-full">{text}</div>
);

const Th = ({ children, right }) => (
  <th className={`label-tiny text-zinc-400 font-medium px-4 py-3 ${right ? "text-right" : "text-left"}`}>{children}</th>
);
